import React from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Breadcrumbs from "../components/Breadcrumbs";
import { ArrowRight } from "lucide-react";
import { languagePickMeta } from "../lib/seo";

/**
 * `/{game}` — language picker.
 *
 * Owner directive 2026-02-01: clicking Pokemon on the home page lands
 * here (NOT straight to sets). Two blocks: English (active) and
 * Japanese (disabled, "Coming soon"). Japanese has NO route yet — do
 * not link the disabled block anywhere.
 *
 * The `:game` param is validated by the containing route guard —
 * unknown TCG slugs fall through to the app's 404.
 */
export default function LanguageSelectPage() {
  const { game } = useParams();
  const gameLabel = _prettyGameLabel(game);
  const meta = languagePickMeta(game);

  return (
    <div
      className="mx-auto w-full max-w-[1400px] px-5 py-8 md:px-gutter md:py-10"
      data-testid="language-select-page"
    >
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta name="robots" content={meta.robots} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:site_name" content="CardCapture" />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content={meta.ogType} />
        <meta property="og:image" content={meta.ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.ogImage} />
      </Helmet>
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: gameLabel }]} />

      <div className="mt-6">
        <div className="text-eyebrow uppercase text-indigo-text">
          Trading Card Game
        </div>
        <h1 className="mt-1 text-title-page text-ink-text">{gameLabel}</h1>
        <p className="mt-2 max-w-[62ch] text-body text-ink-muted">
          Pick a language catalog. Only English is live today; Japanese
          data will land in a later release.
        </p>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Link
          to={`/${encodeURIComponent(game)}/en/sets`}
          data-testid="language-en-link"
          className="animate-rise group flex items-center gap-5 rounded-card border border-ink-chip bg-ink-surface p-5 transition-colors duration-cc ease-cc-ease hover:border-ink-muted"
        >
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-card border border-ink-chip bg-ink-raised text-title-section text-ink-text">
            EN
          </div>
          <div className="flex-1">
            <div className="text-eyebrow uppercase text-ink-muted">Active</div>
            <div className="mt-1 text-title-section text-ink-text">English</div>
            <div className="mt-1 text-meta text-ink-muted">
              Full catalog, live search, community showcases
            </div>
          </div>
          <ArrowRight
            size={18}
            className="text-ink-muted transition-colors duration-cc ease-cc-ease group-hover:text-ink-text"
          />
        </Link>

        <div
          data-testid="language-ja-disabled"
          aria-disabled="true"
          className="animate-rise flex items-center gap-5 rounded-card border border-ink-chip bg-ink-surface p-5 opacity-60"
        >
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-card border border-ink-chip bg-ink-raised text-title-section text-ink-muted">
            JA
          </div>
          <div className="flex-1">
            <div className="text-eyebrow uppercase text-ink-muted">
              Coming soon
            </div>
            <div className="mt-1 text-title-section text-ink-muted">
              Japanese
            </div>
            <div className="mt-1 text-meta text-ink-muted">
              Import + language switch land in a later release.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function _prettyGameLabel(slug) {
  if (slug === "pokemon") return "Pokémon";
  return slug.charAt(0).toUpperCase() + slug.slice(1);
}
